<template lang="pug">
  .claim-table-container(:class="{ 'has-data': hasData, 'selectable': activeTab !== 7 }")
    //- Error State
    div(v-if="isError")
      yz-empty-state-card(icon="mdi-alert-circle-outline" icon-color="yz-danger-500" :title="$t('error_message.unable_to_load_data')" :description="$t('error_message.please_try_again')" :action-label="$t('retry')" @action-click="fetchClaims")

    //- Filter Row and Data table
    div(v-else)
      //- Filter Row
      claim-filter-row.mt-4(ref="claimFilterRow" :filters.sync="filterParams" @update:filters="onFilterChange" @filters-serialized="onFiltersSerialized")

      //- Data table
      yz-data-table.mt-4(:headers="tableHeaders" :items="claimsData" item-key="id" :loading="loading" :server-items-length="totalClaims" :show-select="activeTab !== 7" no-outer-border, radius="0" disable-sort, body-height="52px", header-height="36px", :item-class="getRowClass" @update:sort-by="sortBy = $event" @update:sort-desc="sortIsDesc = $event" @click:row="onViewClaim")
        template(v-slot:header.data-table-select)
          yz-checkbox(:indeterminate="someClaimSelected" :value="allClaimSelected" @change="onToggleSelectAll")

        template(v-slot:item.data-table-select="{ item }")
          yz-checkbox(v-if="item.status !== 'cancelled'" :value="!!selectedClaims.find(selected => selected.id === item.id)" @change="onToggleSelectClaim(item)" @click.native.stop)

        template(v-slot:item.amount="{ item }")
          span {{ item.amount | displayPrice($currency) }}

        template(v-slot:item.panel="{ item }")
          yz-badge(v-if="item.panel" border size="sm") {{ item.panel }}

        template(v-slot:item.patientName="{ item }")
          div(v-if="item.isMultiplePatients")
            .yz-regular-body-sm {{ $t('panel.multiple_patients') }}
            .yz-regular-body-xs ({{ item.totalInvoiceCount }} {{ $t('panel.invoices') }})
          span(v-else-if="item.patientName") {{ item.patientName }}

        template(v-slot:item.updatedBy="{ item }")
          yz-badge(v-if="item.updatedBy" border size="sm")
            yz-avatar.mr-1(size="12")
              yz-img(v-if="item.updatedBy.profile_image && item.updatedBy.profile_image.file" :src="`https://img.yezza.io/50x50/${item.updatedBy.profile_image.path}`")
              yz-icon(v-else color="yz-neutral-500") $yzi-user-03
            span {{ item.updatedBy.name }}

        template(v-slot:item.status="{ item }")
          claim-status-badge(v-if="item.status" :status="item.status" size="sm" dot-size="6px")

        template(v-slot:item.actions="{ item }")
          yz-menu(v-if="item.status !== 'cancelled'" offset-y close-on-click left attach)
            template(v-slot:activator="{ on, attrs }")
              yz-btn(icon-only, icon-color="yz-neutral-500" v-bind="attrs" v-on="on" icon="$yzi-dots-horizontal")
            yz-list
              yz-list-item(@click.stop="onViewClaim(item)")
                div {{ $t('panel.view_claim') }}

              yz-divider

              template(v-if="activeTab !== 6 && item.status !== 'received'")
                yz-list-item(@click.stop="onRevertClaim(item)")
                  div {{ $t('panel.revert_claim_to_invoice') }}

                yz-divider

              yz-list-item(@click.stop="onDownloadInvoice(item)")
                div {{ $t('panel.download_invoice') }}

              template(v-if="item.status === 'received'")
                yz-divider

                yz-list-item(@click.stop="onDownloadReceipt(item)")
                  div {{ $t('panel.download_receipt') }}

              yz-divider

              yz-list-item(@click.stop="onSendClaim(item)")
                div {{ $t('panel.send_claim') }}

      //- Pagination
      .d-flex.justify-end.pt-3
        yz-pagination(v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalClaims" @input="fetchClaims")
      // Sortable
      yz-data-table-sorter(v-model="ordering" :headers="tableHeaders" :sort-by="sortBy" :is-desc="sortIsDesc" @input="onUpdateOrdering")

    //- Revert Claim Dialog
    revert-claim-to-invoice-dialog(v-if="showRevertDialog" v-model="showRevertDialog" :parent-claim="selectedClaim" @close="onRevertClosed" @confirm="onRevertConfirmed")

    //- Send Panel Claim Dialog
    send-claim-dialog(v-if="showSendClaimDialog" v-model="showSendClaimDialog" :claim-id="selectedClaimId" @close="onSendClaimClosed")

    //- Download Invoice Dialog
    download-claim-invoice-dialog(v-if="openSelectInvoiceDialog" v-model="openSelectInvoiceDialog" :claim="selectedClaimForDownload" @close="onDownloadDialogClosed")

    //- Download Receipt Dialog
    download-claim-receipt-dialog(v-if="openSelectReceiptDialog" v-model="openSelectReceiptDialog" :claim="selectedClaimForDownload" @close="onDownloadDialogClosed")

    //- Claim Selection Actions (Sticky)
    claim-selection-actions(:selected-items="selectedClaims" :active-tab="activeTab" :show="selectedClaims.length > 0" :is-downloading="isDownloadingPanelClaim" @download-panel-claim="onDownloadPanelClaim" @updated="onSelectionActionsUpdated")
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { DateTime } from 'luxon'
import RevertClaimToInvoiceDialog from '@/components/clinic/panel/dialogs/RevertClaimToInvoiceDialog.vue'
import SendClaimDialog from '@/components/clinic/panel/dialogs/SendClaimDialog.vue'
import ClaimSelectionActions from '@/components/clinic/panel/claims/ClaimSelectionActions.vue'
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'
import ClaimFilterRow from '@/components/clinic/panel/claims/ClaimFilterRow.vue'
import DownloadClaimInvoiceDialog from '@/components/clinic/panel/dialogs/DownloadClaimInvoiceDialog.vue'
import DownloadClaimReceiptDialog from '@/components/clinic/panel/dialogs/DownloadClaimReceiptDialog.vue'

export default {
  components: { RevertClaimToInvoiceDialog, SendClaimDialog, ClaimSelectionActions, ClaimStatusBadge, ClaimFilterRow, DownloadClaimInvoiceDialog, DownloadClaimReceiptDialog },
  props: {
    activeTab: {
      type: Number,
      required: true
    },
    searchText: {
      type: String,
      default: null
    },
    initialPage: {
      type: Number,
      default: 1
    },
    initialFilters: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      // Page state
      loading: false,
      isError: false,
      isInitializing: true,
      // Pagination
      currentPage: this.initialPage,
      pageSize: 10,
      // Sorting
      sortBy: null,
      sortIsDesc: false,
      ordering: null,
      // Data
      totalClaims: 0,
      claimsData: [],
      // Dialog states
      showRevertDialog: false,
      showSendClaimDialog: false,
      openSelectInvoiceDialog: false,
      openSelectReceiptDialog: false,
      // Selection state
      selectedClaims: [],
      selectedClaim: null,
      // Filters
      filterParams: {},
      // Selected claim for download
      selectedClaimForDownload: null,
      // Loading states
      isDownloadingPanelClaim: false
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany',
      dates: 'clinic/getPanelClaimDates',
      beginDate: 'clinic/getPanelClaimBeginDate',
      untilDate: 'clinic/getPanelClaimUntilDate',
      fields: 'clinic/getPanelClaimFields'
    }),
    tableHeaders() {
      const enabledFields = this.fields.filter(field => field.enable)
      const headers = enabledFields.map(field => ({
        text: field.field_name.toUpperCase(),
        value: field.id,
        sort_query_param: field.sort_query_param,
        sortable: field.sortable !== false
      }))
      // Only add actions column if not on Cancelled tab
      if (this.activeTab !== 7) {
        headers.push({ text: '', value: 'actions', sortable: false, align: 'end' })
      }
      return headers
    },
    allClaimSelected() {
      const selectableClaims = this.claimsData.filter(claim => claim.status !== 'cancelled')
      return selectableClaims.length > 0 && selectableClaims.every(claim => this.selectedClaims.find(selected => selected.id === claim.id))
    },
    someClaimSelected() {
      const selectableClaims = this.claimsData.filter(claim => claim.status !== 'cancelled')
      const hasSelected = selectableClaims.some(claim => this.selectedClaims.find(selected => selected.id === claim.id))
      return hasSelected && !this.allClaimSelected
    },
    selectedClaimId() {
      return this.selectedClaim?.id ?? null
    },
    hasData() {
      return this.claimsData.length > 0
    }
  },
  async mounted() {
    // Initialize filters from initial prop if provided
    if (this.initialFilters) {
      // Deserialize and set filterParams immediately so they're available for first fetch
      this.filterParams = this.deserializeFilters(this.initialFilters)

      // Then initialize the filter UI components
      if (this.$refs.claimFilterRow) {
        await this.$refs.claimFilterRow.initializeFiltersFromProps(this.initialFilters)
      }
    }
    await this.fetchClaims()
    // Mark initialization complete to allow filter changes to trigger refetch
    this.isInitializing = false
  },
  watch: {
    activeTab() {
      this.selectedClaims = []
      this.resetPageAndRefetch()
    },
    dates() {
      this.resetPageAndRefetch()
    },
    currentPage(newPage) {
      this.$emit('page-changed', newPage)
    },
    initialPage(newPage) {
      // Sync with parent when initialPage prop changes (from browser back/forward)
      if (this.currentPage !== newPage) {
        this.currentPage = newPage
      }
    },
    async initialFilters(newFilters) {
      // Handle filter changes from browser back/forward navigation
      if (!newFilters) {
        // Filters cleared - check if we already have empty filters
        const currentFiltersEmpty = !this.filterParams || Object.keys(this.filterParams).length === 0

        if (currentFiltersEmpty) {
          // Already empty, no need to refetch
          return
        }

        // Clear filters and refetch
        this.filterParams = {}
        if (this.$refs.claimFilterRow) {
          await this.$refs.claimFilterRow.initializeFiltersFromProps(null)
        }
        this.resetPageAndRefetch()
        return
      }

      const deserializedFilters = this.deserializeFilters(newFilters)
      const currentFiltersJson = JSON.stringify(this.filterParams)
      const newFiltersJson = JSON.stringify(deserializedFilters)

      // Only update if filters actually changed
      if (currentFiltersJson !== newFiltersJson) {
        this.filterParams = deserializedFilters
        if (this.$refs.claimFilterRow) {
          await this.$refs.claimFilterRow.initializeFiltersFromProps(newFilters)
        }
        this.resetPageAndRefetch()
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    deserializeFilters(base64String) {
      // Deserialize base64 encoded string back to filters object
      if (!base64String) {
        return {}
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        this.showMessage({
          message: this.$t('panel.filter_parameters_corrupted'),
          color: 'warning'
        })
        return {}
      }
    },
    resetPageAndRefetch() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
        return
      }

      this.fetchClaims()
    },
    async fetchClaims() {
      this.loading = true
      this.isError = false
      const params = {
        page: this.currentPage,
        page_size: this.pageSize,
        ordering: this.ordering || '-created',
        search: this.searchText
      }

      // Add tab-specific filtering
      const tabFilters = this.getTabFilters()
      if (tabFilters) {
        Object.assign(params, tabFilters)
      }

      // Add date range filtering
      if (this.beginDate && this.untilDate) {
        params.begin = this.beginDate
        params.until = this.untilDate
      }

      // Add filter params from FilterRow
      if (this.filterParams && Object.keys(this.filterParams).length > 0) {
        Object.assign(params, this.filterParams)
      }

      try {
        const response = await this.$axios.get(`/panels/${this.company.slug}/claims`, { params })
        this.claimsData = response.data.results.map(claim => {
          const statusDate = this.getStatusDate(claim)
          const statusAmount = this.getStatusAmount(claim)
          return {
            originalClaim: claim,
            rid: claim.rid,
            id: claim.id,
            amount: statusAmount,
            claimNo: claim.rid,
            panel: claim.panel?.name,
            patientName: claim.patient_name,
            isMultiplePatients: claim.type !== 'single',
            totalInvoiceCount: claim.invoices.length,
            status: claim.status,
            date: this.formatDate(statusDate),
            remarks: claim.remarks,
            updatedBy: claim.updated_by
          }
        })
        this.totalClaims = response.data.count
      } catch (error) {
        this.isError = true
      } finally {
        this.loading = false
      }
    },
    getTabFilters() {
      // Map tab index to API filter parameters
      const tabMap = {
        0: null, // All - no filter
        3: { status: 'submitted' },
        4: { status: 'approved' },
        5: { status: 'rejected' },
        6: { status: 'received' },
        7: { status: 'cancelled' }
      }
      return tabMap[this.activeTab] || null
    },
    formatDate(dateString) {
      if (!dateString) return null
      const date = DateTime.fromISO(dateString)
      return date.toFormat('d MMMM yyyy')
    },
    getStatusDate(claim) {
      // Return the appropriate date based on the claim's status
      const statusDateMap = {
        submitted: claim.submitted_date,
        approved: claim.approved_date,
        rejected: claim.rejected_date,
        received: claim.received_date,
        cancelled: claim.submitted_date
      }

      // Return the mapped date or fallback to submitted_date for other statuses
      return statusDateMap[claim.status] || null
    },
    getStatusAmount(claim) {
      // Return the appropriate amount based on the claim's status
      const statusAmountMap = {
        submitted: claim.submitted_amount,
        approved: claim.approved_amount,
        rejected: claim.rejected_amount,
        received: claim.received_amount,
        cancelled: claim.submitted_amount
      }

      // Return the mapped amount or fallback to submitted_amount
      return statusAmountMap[claim.status] || claim.submitted_amount || 0
    },
    // Action handlers
    onViewClaim(item) {
      if (this.activeTab === 7) return
      this.$router.push(`/admin/clinic/panels/claim/${item.id}`, { params: { hasLastPath: true } })
    },
    onRevertClaim(item) {
      this.selectedClaim = item.originalClaim
      this.showRevertDialog = true
    },
    onRevertClosed() {
      this.selectedClaim = null
    },
    onRevertConfirmed() {
      this.selectedClaim = null
      this.fetchClaims()
      this.$emit('updated')
    },
    async onDownloadPanelClaim() {
      if (!this.selectedClaims || this.selectedClaims.length === 0) {
        await this.showMessage(['error', 'No claims selected'])
        return
      }

      this.isDownloadingPanelClaim = true

      try {
        if (this.selectedClaims.length === 1) {
          // Single claim: Use OTK + window.open
          const claim = this.selectedClaims[0]
          const { data: otkData } = await this.$axios.post(`users/${this.user.id}/request_otk`)
          const key = otkData.key

          // Determine base URL
          let baseUrl = process.env.VUE_APP_BASE_URL
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            baseUrl = 'https://api-test.yezza.io'
          } else if (process.env.VUE_APP_ENV === 'prod') {
            baseUrl = 'https://export.yezza.io'
          }

          // Open PDF in new tab
          const url = `${baseUrl}/panels/${this.company.slug}/claims/${claim.pid}/preview_document?key=${key}`
          window.open(url, '_blank')

          await this.showMessage(['success', this.$t('panel.download_started')])
        } else {
          // Multiple claims: Use bulk download endpoint with blob (like DownloadClaimInvoiceDialog)
          const claimPids = this.selectedClaims.map(claim => claim.pid).join(',')
          const params = {
            ids: claimPids
          }

          // Download as blob to handle zip file
          const response = await this.$axios.get(`/panels/${this.company.slug}/claims/download`, {
            params,
            responseType: 'blob'
          })

          // Extract filename from content-disposition header
          let filename = `claims_${Date.now()}.zip`
          const contentDisposition = response.headers['content-disposition']
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/"/g, '')
            }
          }

          // Create blob and trigger download
          const blob = new Blob([response.data])
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = filename
          link.click()
          window.URL.revokeObjectURL(link.href)

          await this.showMessage(['success', this.$t('panel.download_started')])
        }
      } catch (error) {
        console.error('Error downloading panel claims:', error)
        await this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDownloadingPanelClaim = false
      }
    },
    async onDownloadInvoice(item) {
      const claim = item.originalClaim
      if (!claim.invoices || claim.invoices.length === 0) {
        await this.showMessage(['warning', 'No invoices found for this claim'])
        return
      }

      this.selectedClaimForDownload = claim
      this.openSelectInvoiceDialog = true
    },
    async onDownloadReceipt(item) {
      const claim = item.originalClaim
      if (!claim.invoices || claim.invoices.length === 0) {
        await this.showMessage(['warning', 'No invoices found for this claim'])
        return
      }

      this.selectedClaimForDownload = claim
      this.openSelectReceiptDialog = true
    },
    onSendClaim(item) {
      this.selectedClaim = item.originalClaim
      this.showSendClaimDialog = true
    },
    onUpdateOrdering() {
      this.resetPageAndRefetch()
    },
    // Selection methods
    onToggleSelectAll() {
      if (this.allClaimSelected) {
        // Remove current page items from selection (excluding cancelled)
        this.claimsData.forEach(claim => {
          if (claim.status !== 'cancelled') {
            const existingIndex = this.selectedClaims.findIndex(selected => selected.id === claim.id)
            if (existingIndex !== -1) {
              this.selectedClaims.splice(existingIndex, 1)
            }
          }
        })
      } else {
        // Add current page items to selection (avoid duplicates and exclude cancelled)
        this.claimsData.forEach(claim => {
          if (claim.status !== 'cancelled') {
            const alreadySelected = this.selectedClaims.find(selected => selected.id === claim.id)
            if (!alreadySelected) {
              this.selectedClaims.push(claim.originalClaim)
            }
          }
        })
      }
    },
    onToggleSelectClaim(item) {
      // Don't allow selection on cancelled tab or for cancelled claims
      if (this.activeTab === 7 || item.status === 'cancelled') {
        return
      }

      const existingIndex = this.selectedClaims.findIndex(selected => selected.id === item.id)
      if (existingIndex !== -1) {
        this.selectedClaims.splice(existingIndex, 1)
      } else {
        this.selectedClaims.push(item.originalClaim)
      }
    },
    onSendClaimClosed() {
      this.selectedClaim = null
    },
    // Selection action handlers
    onSelectionActionsUpdated() {
      this.selectedClaims = []
      this.fetchClaims()
      this.$emit('updated')
    },
    // Filter handlers
    onFilterChange(filters) {
      this.filterParams = filters
      // Don't refetch during initial mount to avoid double API call
      if (!this.isInitializing) {
        this.resetPageAndRefetch()
      }
    },
    onFiltersSerialized(serialized) {
      // Emit to parent (Claims.vue) to update URL
      this.$emit('filters-serialized', serialized)
    },
    onDownloadDialogClosed() {
      this.selectedClaimForDownload = null
      this.openSelectInvoiceDialog = false
      this.openSelectReceiptDialog = false
    },
    getRowClass(item) {
      return item.status === 'cancelled' ? 'cancelled-row' : ''
    }
  }
}
</script>

<style scoped>
.claim-table-container {
  position: relative;
}
.claim-table-container.has-data.selectable ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
.claim-table-container.has-data.selectable ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr.cancelled-row:hover > td {
  cursor: default !important;
  background: transparent !important;
}
</style>
